import { ReactElement, useEffect, useRef } from "react"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface PopoverDialogProps {
  isOpen: boolean
  onClose: () => void
  children: ReactElement
  className?: string
}

const PopoverDialog = ({ isOpen, onClose, children, className = "" }: PopoverDialogProps) => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const isInsideRadixPortal = (target: Element) =>
      !!(
        target.closest("[data-radix-popper-content-wrapper]") ||
        target.closest("[data-radix-portal]") ||
        target.closest("[role='listbox']") ||
        target.closest("[role='dialog']") ||
        target.closest("[role='alertdialog']") ||
        // Radix Dialog overlay (the backdrop) is a sibling of the dialog
        // content, so closest("[role='dialog']") misses it. Clicking the
        // overlay dismisses the child dialog; we must not also close the
        // parent PopoverDialog.
        target.closest("[data-radix-dialog-overlay]") ||
        target.closest("[data-radix-alert-dialog-overlay]")
      )

    const hasOpenRadixPortal = () =>
      !!document.querySelector(
        "[data-radix-popper-content-wrapper]," +
        "[data-state='open'][role='listbox']," +
        "[data-state='open'][role='dialog']," +
        "[data-state='open'][role='alertdialog']",
      )

    // Use pointerdown in the capture phase so this runs BEFORE Radix
    // dismisses its own popper (Select/Popover). Otherwise the portal
    // is already removed by the time a bubbled mousedown reaches us,
    // and we'd incorrectly close the dialog on outside-select clicks.
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target) return
      if (popoverRef.current && popoverRef.current.contains(target)) return
      if (isInsideRadixPortal(target)) return
      if (hasOpenRadixPortal()) return

      onClose()
    }

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!hasOpenRadixPortal()) {
          onClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener("pointerdown", handlePointerDown, true)
      document.addEventListener("keydown", handleEscKey)
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true)
      document.removeEventListener("keydown", handleEscKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      ref={popoverRef}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "animate-fadeIn absolute left-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border bg-popover shadow-md outline-none",
        isHebrew ? "rtl right-0" : "ltr left-0",
        className,
      )}
    >
      {children}
    </div>
  )
}

export default PopoverDialog